/* InlineCreateModal — in-drawer generator that keeps the teacher inside the live room.
 *
 * Renders a compact form for Quiz / Story / Slides / Game / Diagram generation.
 * Uses the same backend endpoints as the standalone AI pages, but never navigates
 * away — the teacher stays connected to the video call while the asset is being
 * built.
 *
 * Props:
 *   - open, onClose
 *   - kind: "quiz" | "story" | "slides" | "game" | "diagram"
 *   - subject: { id, name, grade } — subject linked to the class
 *   - onCreated(kindKey, asset): parent hook to inject the new asset into the drawer's
 *     scoped list.
 *   - onShareLive(kindKey, asset): "Share now" pushes the asset overlay live.
 */
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Sparkles, Share2, CheckCircle2, ClipboardList, ScrollText, Layers as LayersIcon, Crown, ImageIcon, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AttachmentPicker from "@/components/quiz/AttachmentPicker";
import QuizPreviewEditor from "@/components/quiz/QuizPreviewEditor";

const KIND_META = {
  quiz:    { title: "Quiz",    icon: ClipboardList, credits: 6, endpoint: "/ai/quiz/interactive",     listKey: "quizzes",     resultKey: "quiz" },
  story:   { title: "Story",   icon: ScrollText,    credits: 3, endpoint: "/ai/story/generate",       listKey: "stories",     resultKey: "story" },
  slides:  { title: "Slides",  icon: LayersIcon,    credits: 4, endpoint: "/ai/slides/generate",      listKey: "slide_decks", resultKey: "deck" },
  game:    { title: "Champ Game", icon: Crown,      credits: 6, endpoint: "/ai/millionaire/generate", listKey: "games",       resultKey: "game" },
  diagram: { title: "Diagram", icon: ImageIcon,     credits: 4, endpoint: "/ai/image",                listKey: "library",     resultKey: "content" },
};

// Stepped progress messages that cycle while the LLM is thinking — makes the
// 10-20s wait feel alive (real streaming isn't wired end-to-end yet).
const PROGRESS_STEPS = {
  quiz:    ["Reading the topic…", "Drafting questions…", "Verifying answers…", "Formatting for players…"],
  story:   ["Outlining scenes…", "Writing the narrative…", "Picking illustrations…", "Wrapping up…"],
  slides:  ["Structuring the deck…", "Filling each slide…", "Choosing a colour theme…", "Polishing layout…"],
  game:    ["Setting the ladder…", "Building 13 questions…", "Assigning difficulty tiers…", "Locking rewards…"],
  diagram: ["Composing the scene…", "Placing labels…", "Applying colours…", "Rendering the image…"],
};

export default function InlineCreateModal({ open, onClose, kind, subject, onCreated, onShareLive }) {
  const meta = KIND_META[kind];
  const [topic, setTopic] = useState("");
  const [num, setNum] = useState(5);
  const [difficulty, setDifficulty] = useState("medium");
  const [theme, setTheme] = useState("indigo");
  const [quizStyle, setQuizStyle] = useState("mcq"); // mcq | drag_drop | fill_blank
  const [instructions, setInstructions] = useState("");
  const [attachmentText, setAttachmentText] = useState("");
  const [draft, setDraft] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [progressPct, setProgressPct] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");
  const [recent, setRecent] = useState([]);
  const progressTimerRef = useRef(null);

  // Reset internal state whenever the modal is opened for a new kind
  useEffect(() => {
    if (open) {
      setTopic("");
      setNum(kind === "slides" ? 8 : kind === "game" ? 13 : 5);
      setDifficulty("medium");
      setTheme("indigo");
      setQuizStyle("mcq");
      setInstructions("");
      setAttachmentText("");
      setDraft(null);
      setPublishing(false);
      setDone(null);
      setProgressPct(0);
      setProgressMsg("");
      setRecent([]);
    }
  }, [open, kind]);

  // When the modal opens, fetch a handful of assets the teacher has already
  // made for this subject/kind — surfaced as chips so they can reuse instead
  // of re-generating.
  useEffect(() => {
    if (!open || !meta) return;
    let cancelled = false;
    (async () => {
      try {
        let items = [];
        const sid = subject?.id;
        if (!sid && kind !== "diagram") return;
        if (kind === "quiz")    { const { data } = await api.get("/ai/quiz",       { params: { subject_id: sid } }); items = data.quizzes || []; }
        if (kind === "story")   { const { data } = await api.get("/ai/story",      { params: { subject_id: sid } }); items = data.stories || []; }
        if (kind === "slides")  { const { data } = await api.get("/ai/slides",     { params: { subject_id: sid } }); items = data.decks   || data.slide_decks || []; }
        if (kind === "game")    { const { data } = await api.get("/ai/millionaire",{ params: { subject_id: sid } }); items = data.games   || []; }
        if (kind === "diagram") { const { data } = await api.get("/ai/library");                                    items = (data.items || []).filter((i) => i.kind === "diagram" || i.kind === "image"); }
        if (!cancelled) setRecent(items.slice(0, 4));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, kind, subject?.id]);

  // Cleanup timer on unmount / close
  useEffect(() => {
    return () => { if (progressTimerRef.current) clearInterval(progressTimerRef.current); };
  }, []);

  if (!open || !meta) return null;

  const Icon = meta.icon;
  const steps = PROGRESS_STEPS[kind] || ["Working…"];

  const startProgress = () => {
    setProgressPct(4);
    setProgressMsg(steps[0]);
    let elapsed = 0;
    // Progress ceils at 92% until server responds so the bar always has room to
    // finish. Each step lives ~5s; if the server takes longer the bar creeps
    // slower toward the ceiling.
    progressTimerRef.current = setInterval(() => {
      elapsed += 0.4;
      setProgressPct((p) => {
        const target = Math.min(92, 4 + elapsed * 7);
        return p + (target - p) * 0.18;
      });
      const idx = Math.min(steps.length - 1, Math.floor(elapsed / 4));
      setProgressMsg(steps[idx]);
    }, 400);
  };

  const stopProgress = (success) => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = null;
    if (success) { setProgressPct(100); setProgressMsg("Done"); }
  };

  const buildPayload = () => {
    const common = {
      subject: subject?.name || "",
      subject_id: subject?.id || "",
      grade: subject?.grade || "",
      topic,
    };
    if (kind === "quiz")   return { ...common, num_questions: Number(num) || 5, difficulty, category: quizStyle, instructions, attachment_text: attachmentText, save: false };
    if (kind === "slides") return { ...common, prompt: [topic, instructions, attachmentText && `Build from this source material:\n${attachmentText.slice(0, 8000)}`].filter(Boolean).join("\n\n"), n: Number(num) || 8, theme };
    if (kind === "game")   return {
      ...common, n: Number(num) || 13,
      pages: [{ order: 1, ocr_text: attachmentText.slice(0, 6000), summary: [topic, instructions].filter(Boolean).join(" — ") }],
    };
    if (kind === "diagram") return { prompt: `Educational diagram of ${topic}. Clean labels, textbook style.${instructions ? ` ${instructions}` : ""}`, kind: "diagram", size: "1024x1024" };
    // story
    return {
      ...common,
      pages: [{ order: 1, ocr_text: attachmentText.slice(0, 6000), summary: [topic, instructions].filter(Boolean).join(" — ") }],
      with_images: false,
    };
  };

  const generate = async () => {
    if (!topic.trim()) return toast.error("Give the topic first");
    if (kind !== "diagram" && !subject?.id) {
      return toast.error("Link a subject to this class first");
    }
    setBusy(true); setDone(null);
    startProgress();
    try {
      const { data } = await api.post(meta.endpoint, buildPayload());
      const asset = data[meta.resultKey];
      stopProgress(true);
      // Small delay so students see the bar fill before the success state swaps in
      await new Promise((r) => setTimeout(r, 320));
      if (kind === "quiz" && data.draft) {
        // Professional workflow — teacher reviews & edits before anything is saved
        setDraft(asset);
      } else {
        setDone(asset);
        onCreated?.(meta.listKey, asset);
        toast.success(`${meta.title} ready`);
      }
    } catch (e) {
      stopProgress(false);
      toast.error(e.response?.data?.detail || `${meta.title} generation failed`);
    } finally { setBusy(false); }
  };

  const publishDraft = async (edited) => {
    setPublishing(true);
    try {
      const { data } = await api.post("/ai/quiz/save", {
        title: edited.title,
        subject: subject?.name || "",
        topic,
        grade: subject?.grade || "",
        subject_id: subject?.id || "",
        category: quizStyle,
        questions: edited.questions,
      });
      setDraft(null);
      setDone(data.quiz);
      onCreated?.(meta.listKey, data.quiz);
      toast.success("Quiz published");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const shareNow = () => {
    if (!done) return;
    onShareLive?.(meta.listKey, done);
    onClose?.();
  };

  const downloadImage = () => {
    if (!done) return;
    const url = done.url || (done.data ? `data:image/png;base64,${done.data}` : "");
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(topic || "diagram").slice(0, 40)}.png`;
    a.click();
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="drawer-create-modal"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className={`w-full ${draft && !done ? "max-w-3xl" : "max-w-lg"} bg-[#10141C] border border-white/10 rounded-lg shadow-2xl max-h-[92vh] overflow-y-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-5 border-b border-white/10 flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-[#FFD600]/15 text-[#FFD600] flex items-center justify-center">
                <Icon size={16} />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold">/ create · in room</div>
                <h3 className="font-display font-bold text-xl tracking-tighter text-white mt-0.5">
                  New {meta.title.toLowerCase()}
                </h3>
                <div className="text-[11px] text-white/50 mt-1 font-mono">
                  {subject?.name ? `Scoped to ${subject.name}` : "No subject linked"}
                  {" · "}{meta.credits} credits
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="modal-close"><X size={18} /></button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-3">
            {!done && !busy && (
              <>
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/60">Topic *</span>
                  <input
                    autoFocus
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && topic.trim()) generate(); }}
                    placeholder={
                      kind === "quiz" ? "Photosynthesis"
                      : kind === "slides" ? "Newton's Laws"
                      : kind === "game" ? "World War II"
                      : kind === "diagram" ? "Solar system with labelled planets"
                      : "The Water Cycle"
                    }
                    className="mt-1 w-full bg-white/5 border border-white/15 hover:border-white/30 focus:border-[#FFD600] rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none"
                    data-testid="modal-topic-input"
                  />
                </label>

                {kind === "quiz" && (
                  <>
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-white/60">Quiz style</span>
                      <div className="mt-1 grid grid-cols-3 gap-2" data-testid="modal-quiz-styles">
                        {[
                          { id: "mcq",        label: "Multiple choice", desc: "4 options",       tint: "#7C3AED" },
                          { id: "drag_drop",  label: "Drag & Drop",     desc: "Match zones",     tint: "#0EA5E9" },
                          { id: "fill_blank", label: "Fill blank",      desc: "Type answer",     tint: "#10B981" },
                        ].map((s) => (
                          <button key={s.id} type="button" onClick={() => setQuizStyle(s.id)}
                            data-testid={`modal-quiz-style-${s.id}`}
                            className={`p-2.5 rounded-md border text-left transition-colors ${quizStyle === s.id ? "border-[#FFD600] bg-[#FFD600]/10 text-white" : "border-white/10 hover:border-white/30 text-white/70"}`}>
                            <div className="w-1 h-1 rounded-full" style={{ background: s.tint }} />
                            <div className="text-[12px] font-semibold mt-1.5">{s.label}</div>
                            <div className="text-[10px] font-mono text-white/50 mt-0.5">{s.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-white/60"># Questions</span>
                        <input type="number" min={3} max={20} value={num} onChange={(e) => setNum(e.target.value)}
                          className="mt-1 w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white outline-none" />
                      </label>
                      <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-white/60">Difficulty</span>
                        <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}
                          className="mt-1 w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white outline-none">
                          <option value="easy" className="text-black">Easy</option>
                          <option value="medium" className="text-black">Medium</option>
                          <option value="hard" className="text-black">Hard</option>
                        </select>
                      </label>
                    </div>
                  </>
                )}

                {kind === "slides" && (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-white/60"># Slides</span>
                      <input type="number" min={3} max={20} value={num} onChange={(e) => setNum(e.target.value)}
                        className="mt-1 w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white outline-none" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-white/60">Theme</span>
                      <select value={theme} onChange={(e) => setTheme(e.target.value)}
                        className="mt-1 w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white outline-none">
                        <option value="indigo" className="text-black">Indigo</option>
                        <option value="emerald" className="text-black">Emerald</option>
                        <option value="rose" className="text-black">Rose</option>
                        <option value="amber" className="text-black">Amber</option>
                      </select>
                    </label>
                  </div>
                )}

                {kind === "game" && (
                  <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-white/60">Ladder length</span>
                    <input type="number" min={8} max={15} value={num} onChange={(e) => setNum(e.target.value)}
                      className="mt-1 w-full bg-white/5 border border-white/15 rounded-md px-3 py-2 text-sm text-white outline-none" />
                    <div className="text-[10px] font-mono text-white/40 mt-1">$100 → $1M ladder · easier at the bottom</div>
                  </label>
                )}

                {kind === "diagram" && (
                  <div className="text-[11px] text-white/50 leading-relaxed">
                    Generates a clean textbook-style illustration you can drop straight into the room. Higher detail = slightly longer wait.
                  </div>
                )}

                <div className="pt-2 border-t border-white/10 mt-2 space-y-2" data-testid="modal-guidance-panel">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/60">Guide the AI (optional)</span>
                  <textarea
                    rows={2}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="e.g. Base it on chapter 4, keep the language simple, use real-life examples…"
                    data-testid="modal-instructions-input"
                    className="w-full bg-white/5 border border-white/15 focus:border-[#FFD600] rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none resize-none"
                  />
                  <AttachmentPicker dark compact onChange={setAttachmentText} />
                </div>

                {recent.length > 0 && (
                  <div className="pt-2 border-t border-white/10 mt-2" data-testid="modal-recent-chips">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-1.5 inline-flex items-center gap-1">
                      <CheckCircle2 size={11} className="text-[#10B981]" />
                      Already made · reuse instead of regenerating
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {recent.map((r) => (
                        <button key={r.id} type="button" onClick={() => { onCreated?.(meta.listKey, r); onShareLive?.(meta.listKey, r); onClose?.(); }}
                          data-testid={`modal-recent-${r.id}`}
                          className="text-[11px] px-2 py-1 rounded-md bg-white/5 hover:bg-[#10B981]/15 border border-white/10 hover:border-[#10B981]/50 text-white/80 hover:text-white transition-colors max-w-[220px] truncate">
                          {r.title || r.topic || "Untitled"}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-2 flex items-center justify-between border-t border-white/10 mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-white/40 inline-flex items-center gap-1">
                    <Sparkles size={11} className="text-[#FFD600]" /> Room stays connected while we build.
                  </div>
                  <button
                    onClick={generate}
                    disabled={!topic.trim()}
                    data-testid="modal-generate-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-semibold hover:bg-[#FFC600] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles size={14} /> Generate ({meta.credits} cr)
                  </button>
                </div>
              </>
            )}

            {busy && (
              // Progress state — stepped messages + animated bar
              <div className="py-6 space-y-4" data-testid="modal-progress-state">
                <div className="flex items-center gap-3">
                  <Loader2 size={18} className="animate-spin text-[#FFD600]" />
                  <div className="text-sm text-white font-medium">{progressMsg}</div>
                </div>
                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden" data-testid="modal-progress-bar">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#FFD600] to-[#F59E0B]"
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">
                  {Math.round(progressPct)}% · your students still see you live
                </div>
              </div>
            )}

            {draft && !busy && !done && (
              // Professional quiz workflow — review & edit the AI draft before publishing
              <div className="py-2" data-testid="modal-draft-review">
                <QuizPreviewEditor draft={draft} dark publishing={publishing} onPublish={publishDraft} onCancel={() => setDraft(null)} />
              </div>
            )}

            {done && !busy && (
              // Success state — offer to share the freshly generated asset live
              <div className="py-6 text-center space-y-4" data-testid="modal-done-state">
                <div className="w-14 h-14 rounded-full bg-[#10B981]/15 text-[#10B981] flex items-center justify-center mx-auto">
                  <CheckCircle2 size={22} />
                </div>
                <div>
                  <div className="text-lg font-display font-semibold text-white">{done.title || meta.title}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {kind === "quiz"    && `${(done.questions || []).length} question${(done.questions || []).length === 1 ? "" : "s"}`}
                    {kind === "slides"  && `${(done.slides || []).length} slides`}
                    {kind === "story"   && `${(done.slides || []).length} scenes`}
                    {kind === "game"    && `${(done.questions || []).length} ladder steps`}
                    {kind === "diagram" && "Image ready"}
                  </div>
                </div>

                {kind === "diagram" && done.url && (
                  <div className="max-w-xs mx-auto border border-white/10 rounded-md overflow-hidden bg-black" data-testid="modal-diagram-preview">
                    <img src={done.url} alt={topic} className="w-full h-auto" />
                  </div>
                )}

                <div className="flex items-center gap-2 justify-center flex-wrap">
                  <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/15 hover:border-white/40 text-sm text-white/80 hover:text-white transition-colors" data-testid="modal-keep-btn">
                    Save for later
                  </button>
                  {kind === "diagram" && (
                    <button onClick={downloadImage} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/20 hover:border-white/60 text-sm text-white/80 hover:text-white transition-colors" data-testid="modal-download-btn">
                      <Download size={14} /> Download
                    </button>
                  )}
                  <button onClick={shareNow} data-testid="modal-share-live-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-semibold hover:bg-[#FFC600] transition-colors">
                    <Share2 size={14} /> Share live now
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
